// ═══════════════════════════════════════════════════════════════
// ScoreCard — ITRIIA BENCH v0.2 Score Visualization
// ═══════════════════════════════════════════════════════════════
//
// Affiche le scoring multi-dimensionnel d'une base d'expertise.
// 5 axes computés + 3 axes LLM-jugés (optionnels).
// Radar visuel CSS + barres horizontales + score global.
//
// Props :
//   - baseId: number
//   - isOwner: boolean — owner peut déclencher le scoring
//   - compact: boolean — mode compact pour les cards de liste
// ═══════════════════════════════════════════════════════════════

import React, { useState, useCallback } from 'react';
import {
    Target, Shield, Layers, Zap, DollarSign,
    Eye, GitBranch, BarChart3, Loader, RefreshCw, Sparkles
} from 'lucide-react';

const API_BASE = '/api/expertise-bases';
const getToken = () => localStorage.getItem('oracle_token');
const authHeaders = () => ({
    Authorization: `Bearer ${getToken()}`,
    'Content-Type': 'application/json',
});

const ACCENT = '#8b5cf6';

// Configuration des axes
const AXES_CONFIG = {
    couverture:      { label: 'Couverture',       icon: Target,    color: '#10b981', description: 'Sections résumées avec succès' },
    profondeur:      { label: 'Profondeur',       icon: Layers,    color: '#3b82f6', description: 'Niveaux de synthèse + qualité L1/L2' },
    compression:     { label: 'Compression',      icon: Zap,       color: '#f59e0b', description: 'Ratio résumé/source optimal (3-20%)' },
    integrite:       { label: 'Intégrité',        icon: Shield,    color: '#ef4444', description: 'Structure complète, 0 erreur' },
    cout_efficacite: { label: 'Coût-efficacité',  icon: DollarSign,color: '#22c55e', description: 'Qualité / coût (log-normalisé)' },
};

const QUAL_AXES_CONFIG = {
    fidelite:     { label: 'Fidélité',          icon: Eye,        color: '#8b5cf6', description: 'Level2 fidèle aux level1 (LLM-jugé)' },
    coherence:    { label: 'Cohérence',         icon: GitBranch,  color: '#06b6d4', description: 'Récit structuré, progression logique' },
    densite_info: { label: 'Densité info',      icon: BarChart3,  color: '#ec4899', description: 'Information utile / remplissage' },
};

// Score → couleur
function scoreColor(score) {
    if (score >= 8) return '#10b981';
    if (score >= 6) return '#f59e0b';
    if (score >= 4) return '#ef4444';
    return '#6b7280';
}

// Score → label
function scoreLabel(score) {
    if (score >= 9) return 'Excellent';
    if (score >= 8) return 'Très bon';
    if (score >= 7) return 'Bon';
    if (score >= 5) return 'Correct';
    if (score >= 3) return 'Faible';
    return 'Insuffisant';
}

// Barre de score horizontale
function ScoreBar({ axis, config, score, detail }) {
    const Icon = config.icon;
    const pct = Math.min(100, (score / 10) * 100);

    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0' }}>
            <div style={{
                width: 28, height: 28, borderRadius: 8,
                background: `${config.color}15`,
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
                <Icon size={14} style={{ color: config.color }} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 3 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>
                        {config.label}
                    </span>
                    <span style={{
                        fontSize: 13, fontWeight: 700, color: scoreColor(score),
                        fontVariantNumeric: 'tabular-nums',
                    }}>
                        {score.toFixed(1)}
                    </span>
                </div>
                <div style={{
                    height: 5, borderRadius: 3, background: 'var(--card-border)',
                    overflow: 'hidden',
                }}>
                    <div style={{
                        height: '100%', borderRadius: 3,
                        background: `linear-gradient(90deg, ${config.color}88, ${config.color})`,
                        width: `${pct}%`,
                        transition: 'width 0.5s ease',
                    }} />
                </div>
                {detail && (
                    <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2, lineHeight: 1.3 }}>
                        {detail}
                    </div>
                )}
            </div>
        </div>
    );
}

export default function ScoreCard({ baseId, scoringAggregate, isOwner, compact }) {
    const [loading, setLoading] = useState(false);
    const [scores, setScores] = useState(scoringAggregate || null);

    const triggerScoring = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE}/${baseId}/score`, {
                method: 'POST',
                headers: authHeaders(),
            });
            const data = await res.json();
            if (data.success) {
                setScores(data.scoring_aggregate);
            }
        } catch (err) {
            console.error('Scoring failed:', err);
        } finally {
            setLoading(false);
        }
    }, [baseId]);

    const hasScores = scores && scores.global_score > 0;
    const avgScores = scores?.avg_scores || {};
    const hasQualitative = !!(avgScores.qual_fidelite || avgScores.qual_coherence || avgScores.qual_densite_info);

    // ─── Mode compact (pour les cards de liste) ────────────────
    if (compact) {
        if (!hasScores) return null;
        return (
            <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                padding: '2px 7px', borderRadius: 16,
                background: `${scoreColor(scores.global_score)}15`,
                border: `1px solid ${scoreColor(scores.global_score)}30`,
            }}>
                <span style={{
                    fontSize: 10, fontWeight: 700,
                    color: scoreColor(scores.global_score),
                    fontVariantNumeric: 'tabular-nums',
                }}>
                    {scores.global_score.toFixed(1)}
                </span>
                <span style={{ fontSize: 8, color: 'var(--text-muted)', fontWeight: 600 }}>
                    /10
                </span>
            </div>
        );
    }

    // ─── Mode plein ────────────────────────────────────────────
    return (
        <div style={{
            background: 'var(--card-bg)',
            border: '1px solid var(--card-border)',
            borderRadius: 14, padding: '18px 20px',
        }}>
            {/* Header */}
            <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                marginBottom: hasScores ? 16 : 10,
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{
                        width: 32, height: 32, borderRadius: 10,
                        background: `linear-gradient(135deg, ${ACCENT}20, ${ACCENT}08)`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <Target size={16} style={{ color: ACCENT }} />
                    </div>
                    <div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>
                            ITRIIA BENCH v0.2
                        </div>
                        <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>
                            Scoring multi-dimensionnel
                        </div>
                    </div>
                </div>

                {/* Score global badge */}
                {hasScores && (
                    <div style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        padding: '6px 14px', borderRadius: 12,
                        background: `${scoreColor(scores.global_score)}12`,
                        border: `1px solid ${scoreColor(scores.global_score)}30`,
                    }}>
                        <span style={{
                            fontSize: 22, fontWeight: 800,
                            color: scoreColor(scores.global_score),
                            fontVariantNumeric: 'tabular-nums', lineHeight: 1,
                        }}>
                            {scores.global_score.toFixed(1)}
                        </span>
                        <div style={{ fontSize: 10, color: 'var(--text-muted)', lineHeight: 1.2 }}>
                            <div style={{ fontWeight: 600, color: scoreColor(scores.global_score) }}>
                                {scoreLabel(scores.global_score)}
                            </div>
                            <div>/10 · {scores.documents_scored}/{scores.documents_total} docs</div>
                        </div>
                    </div>
                )}
            </div>

            {/* Contenu */}
            {hasScores ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {/* Axes computés */}
                    <div style={{
                        fontSize: 10, fontWeight: 600, color: 'var(--text-muted)',
                        textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4,
                    }}>
                        Axes computés
                    </div>
                    {Object.entries(AXES_CONFIG).map(([key, config]) => {
                        const val = avgScores[key];
                        if (val === undefined || val === null) return null;
                        return <ScoreBar key={key} axis={key} config={config} score={val} />;
                    })}

                    {/* Axes qualitatifs (LLM-jugés) */}
                    {hasQualitative && (
                        <>
                            <div style={{
                                fontSize: 10, fontWeight: 600, color: 'var(--text-muted)',
                                textTransform: 'uppercase', letterSpacing: 0.5,
                                marginTop: 12, marginBottom: 4,
                                display: 'flex', alignItems: 'center', gap: 6,
                            }}>
                                <Sparkles size={10} style={{ color: ACCENT }} />
                                Évaluation qualitative (LLM)
                            </div>
                            {Object.entries(QUAL_AXES_CONFIG).map(([key, config]) => {
                                const val = avgScores[`qual_${key}`];
                                if (val === undefined || val === null) return null;
                                return <ScoreBar key={key} axis={key} config={config} score={val} />;
                            })}
                        </>
                    )}

                    {/* Timestamp */}
                    {scores.scored_at && (
                        <div style={{
                            fontSize: 10, color: 'var(--text-muted)', marginTop: 8,
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        }}>
                            <span>Scoring : {new Date(scores.scored_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                            {isOwner && (
                                <button
                                    onClick={triggerScoring}
                                    disabled={loading}
                                    style={{
                                        background: 'none', border: 'none', cursor: loading ? 'wait' : 'pointer',
                                        color: ACCENT, fontSize: 10, fontWeight: 600,
                                        display: 'flex', alignItems: 'center', gap: 4,
                                        padding: '2px 6px', borderRadius: 6,
                                        opacity: loading ? 0.5 : 1,
                                    }}
                                >
                                    {loading ? <Loader size={10} className="spin" /> : <RefreshCw size={10} />}
                                    Rescorer
                                </button>
                            )}
                        </div>
                    )}
                </div>
            ) : (
                /* Pas encore de scores */
                <div style={{ textAlign: 'center', padding: '12px 0' }}>
                    <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '0 0 10px 0' }}>
                        Aucun score calculé pour cette base
                    </p>
                    {isOwner && (
                        <button
                            onClick={triggerScoring}
                            disabled={loading}
                            style={{
                                background: `linear-gradient(135deg, ${ACCENT}, ${ACCENT}dd)`,
                                color: '#fff', border: 'none', borderRadius: 10,
                                padding: '8px 18px', fontSize: 12, fontWeight: 600,
                                cursor: loading ? 'wait' : 'pointer',
                                display: 'inline-flex', alignItems: 'center', gap: 6,
                                opacity: loading ? 0.7 : 1,
                                transition: 'opacity 0.2s',
                            }}
                        >
                            {loading ? <Loader size={14} className="spin" /> : <Target size={14} />}
                            {loading ? 'Scoring en cours...' : 'Analyser la qualité'}
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}
